import { DateTime } from 'luxon';
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { DATESTR_FMT } from 'tdr-common';
import { HASH_ELEMENT_IDS } from '../../common/hashElementIds';
import DateChecked from '../../icons/date-checked.svg';
import DateUnchecked from '../../icons/date-unchecked.svg';
import TimeChecked from '../../icons/time-checked.svg';
import TimeUnchecked from '../../icons/time-unchecked.svg';
import { AvailabilityGuardContext, serializeSearchFiltersForURL } from '../../routes/AvailabilityGuard';
import { analytics, FILTER_VIEWED } from '../../helpers/analytics';
import { useFilterAnalytics } from '../../helpers/analytics/filterContext';
import { useInViewOnce } from '../../hooks/useInViewOnce';
import Profile2User from 'iconsax-react/dist/esm/Profile2User';
import Clock from 'iconsax-react/dist/esm/Clock';
import Calendar from 'iconsax-react/dist/esm/Calendar';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import { useSessionStorage } from '@/hooks/useSessionStorage';
import { SEARCH_PARAM_SELECTED_KEY } from '@/common/constants';
import { cn } from '@/lib/utils';
import { URLParams } from '../../common/types';
import { useTranslation } from 'react-i18next';

type SidebarFiltersHeaderProps = {
	searchFiltersProp?: SearchFilters;
	classNames?: string;
}

export const SidebarFiltersHeader = ({ searchFiltersProp = { groupSize: 0, date: '', time: '' }, classNames = '' }: SidebarFiltersHeaderProps) => {
	const { t, i18n } = useTranslation();
	const AvailabilityGuardContext = useOutletContext<AvailabilityGuardContext>();
	const restaurant = AvailabilityGuardContext?.restaurant;
	const searchFilters = AvailabilityGuardContext?.searchFilters ? AvailabilityGuardContext?.searchFilters : searchFiltersProp;
	const navigate = useNavigate();
	const { tableSlug } = useParams<URLParams>();
	const [, setClickedFilter] = useSessionStorage(SEARCH_PARAM_SELECTED_KEY, 'groupSize');
	const rootRef = useRef<HTMLDivElement>(null);
	const filterCtx = useFilterAnalytics('global explore');
	useInViewOnce(rootRef, () => analytics.track(FILTER_VIEWED, { ...filterCtx }));

	const handleClick = (hash: string, filterType: string) => {
		analytics.track('Top Bar Filter Parameter Clicked', {
			filterType,
			tableSlug: tableSlug || '',
			currentFilters: {
				groupSize: searchFilters.groupSize,
				date: searchFilters.date,
				time: searchFilters.time
			}
		});

		setClickedFilter(filterType);

		const pathname = tableSlug
			? `/${restaurant.slug}/explore/${tableSlug}/availability`
			: `/${restaurant.slug}/explore/filters`;

		// When navigating to availability to change time, omit time from URL so user picks a new time
		const filtersForNav = filterType === 'time' && tableSlug
			? { ...searchFilters, time: undefined }
			: searchFilters;

		navigate({
			pathname,
			search: serializeSearchFiltersForURL(filtersForNav).toString(),
			hash
		});
	};

	if (!searchFilters) {
		return <></>;
	}
	const isFiltersEmpty = !Object.values(searchFilters).some((filter) => filter !== undefined && filter !== null); // true
	const dateIconURL: string = searchFilters.date ? DateChecked : DateUnchecked;
	const timeIconURL: string = searchFilters.time ? TimeChecked : TimeUnchecked;

	return (
		<div ref={rootRef} className={cn('flex justify-center gap-2 py-1 px-2', classNames)}>
			<FilterIcon
				key={`${searchFilters.groupSize}`}
				icon={<Profile2User size={16} color="#B58D45" />}
				text={`${searchFilters.groupSize}`}
				onClick={() => handleClick(HASH_ELEMENT_IDS.groupSize, 'groupSize')}
			/>
			{isFiltersEmpty || !!searchFilters.date ? (
				<FilterIcon
					key={`${searchFilters.date}-${i18n.language}`}
					icon={<Calendar size={16} color="#B58D45" />}
					text={formatDate(searchFilters.date, i18n.language)}
					onClick={() => handleClick('calendar', 'date')}
					date={searchFilters?.date}
					grow
				/>
			) : null}
			{isFiltersEmpty || !!searchFilters.time ? (
				<FilterIcon
					key={`${searchFilters.time}`}
					icon={<Clock size={16} color="#B58D45" />}
					text={formatTime(searchFilters.time)}
					grow
					onClick={() => handleClick(HASH_ELEMENT_IDS.timeslots, 'time')}
				/>
			) : null}
			{!isFiltersEmpty && (!searchFilters?.time || !searchFilters?.date) ? (
				<>
					<FilterIcon
						key={'params_missing_params'}
						text={!searchFilters?.date ? t('actions.select_date') : t('actions.set_time')}
						icon={
							<img
								src={!!searchFilters?.date ? timeIconURL : dateIconURL}
								alt={`${!!searchFilters.date ? t('labels.time') : t('labels.date')} ${t('labels.unset_icon', { defaultValue: 'unset Icon' })}`}
							/>
						}
						variant='filled'
						styleOverride={{ textTransform: 'capitalize', padding: '10px 7px' }}
						grow
						onClick={() => handleClick(
							!searchFilters?.date ? HASH_ELEMENT_IDS.calendar : HASH_ELEMENT_IDS.timeslots,
							!searchFilters?.date ? 'date' : 'time'
						)}
					/>
				</>
			) : null}

			{/* <SquareIconButton onClick={handleDeleteClick} variant='delete' ariaLabel='Reset Filters' /> */}
		</div>
	);
};

const formatDate = (dateFilter?: string, language?: string) => {
	if (!dateFilter) {
		return '';
	}
	const locale = language === 'fr' ? 'fr-CA' : 'en';
	return DateTime.fromFormat(dateFilter, DATESTR_FMT)
		.setLocale(locale)
		.toLocaleString({ month: 'long', day: 'numeric' });
};

const formatDateShort = (dateFilter?: string, language?: string) => {
	if (!dateFilter) {
		return '';
	}
	const locale = language === 'fr' ? 'fr-CA' : 'en';
	return DateTime.fromFormat(dateFilter, DATESTR_FMT)
		.setLocale(locale)
		.toLocaleString({ month: 'short', day: 'numeric' });
};


const formatTime = (timeFilter?: string) => {
	return timeFilter ? DateTime.fromFormat(timeFilter, 'HH:mm').toFormat('h:mma') : '';
};

type FilterIconProps = {
	icon?: React.ReactNode;
	variant?: 'filled' | 'outlined' | 'text';
	grow?: boolean;
	text?: string;
	ariaLabel?: string;
	onClick?: () => void;
	styleOverride?: React.CSSProperties;
	date?: string
};
const FilterIcon = ({ icon, text, ariaLabel, onClick, styleOverride, grow, date = '' }: FilterIconProps) => {
	const { i18n } = useTranslation();
	const buttonTextRef = useRef<HTMLElement>(null);
	const [displayText, setDisplayText] = useState<string>(text);

	useEffect(() => {
		const checkOverflow = () => {
			const isOverflowing = buttonTextRef?.current?.clientWidth !== buttonTextRef?.current?.scrollWidth;
			if (!!date && isOverflowing) {
				setDisplayText(formatDateShort(date, i18n.language));
			}
		};
		checkOverflow();
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	}, [date, text, buttonTextRef.current, i18n.language]);
	return (
		<button
			onClick={onClick}
			className={`bg-tablz-med-gray-2 rounded-2xl border border-white/10 flex gap-1 p-5 items-center justify-center overflow-hidden ${grow && 'grow'} hover:bg-tablz-med-gray-6`}
			style={{ ...styleOverride }}
			aria-label={ariaLabel}
		>
			{icon}
			<span ref={buttonTextRef} className="font-poppins text-zinc-50 font-semibold overflow-hidden whitespace-nowrap capitalize">{displayText}</span>
		</button>
	);
};
